/**
 * adminApi.real.ts — HTTP wrappers + backend→mock mappers for the admin UI.
 *
 * Maps backend response shapes → MockUser / MockRegistration / AuditEvent so
 * Production providers can consume the same interfaces as the Demo providers.
 *
 * Roles mapping: admin, advisor and wrise pass through; every other backend role
 * is dropped (member, root, etc.).
 */

import { api } from './api'
import type { Data } from '@/types/apiData'
import { isListRole } from './mock/adminApi'
import type {
  MockUser,
  MockRegistration,
  AuditEvent,
  AuditType,
  Role,
  UserListRow,
  RegistrationInput,
  ListParams,
} from './mock/adminApi'

// ── Types ──────────────────────────────────────────────────────────────────

/** Backend user list/detail row shape (from B1-4 Part A). */
interface BackendUserRow {
  id: string
  email: string
  name: string
  isActive: boolean
  forcePasswordChange: boolean
  createdAt: string
  updatedAt: string
  roles: { id: string; name: string }[]
}

/** Registration row; the legacy list/detail routes build it with the same toRegistrationRow as the contract's POST/PUT. */
type BackendRegistrationRow = Data<'createAdvisorRegistration'>

/** Backend audit event row shape (from A5). */
interface BackendAuditRow {
  id: string
  label: string
  date: string
  actor: string
  type?: string | null
  details?: Record<string, unknown> | null
}

// ── Valid audit types ──────────────────────────────────────────────────────

const AUDIT_TYPES = new Set<AuditType>([
  'created', 'invited', 'password', 'role', 'profile', 'bank',
  'session', 'login', 'email', 'status',
])

function isAuditType(v: string | null | undefined): v is AuditType {
  return typeof v === 'string' && AUDIT_TYPES.has(v as AuditType)
}

// ── Mappers ────────────────────────────────────────────────────────────────

/**
 * Map a backend user row → MockUser.
 * Fields the backend does NOT return get defaults:
 *   invitationSentAt / passwordSetAt / inviteExpiresAt / lastLoginAt → null
 *   activeSessions → 0
 * Optional fields (caseCount, deactivatedAt, deactivatedBy, advisorProfile) are
 * OMITTED (exactOptionalPropertyTypes: omitting is correct; null would type-error
 * for caseCount which is `number|undefined`, not `number|null`).
 */
export function toMockUser(row: BackendUserRow): MockUser {
  const roles: Role[] = row.roles.flatMap((r) => {
    if (r.name === 'admin' || r.name === 'advisor' || r.name === 'wrise') return [r.name as Role]
    return []
  })

  return {
    id: row.id,
    name: row.name,
    email: row.email,
    roles,
    isActive: row.isActive,
    invitationSentAt: null,
    passwordSetAt: null,
    inviteExpiresAt: null,
    createdAt: row.createdAt,
    lastLoginAt: null,
    activeSessions: 0,
  }
}

/**
 * Map a backend registration row → MockRegistration.
 * The shapes already align; this is a passthrough with type narrowing.
 */
export function toMockRegistration(row: BackendRegistrationRow): MockRegistration {
  return {
    id: row.id,
    profileId: row.profileId,
    fullName: row.fullName,
    idCardNumber: row.idCardNumber,
    email: row.email,
    phone: row.phone,
    lineId: row.lineId,
    birthDate: row.birthDate,
    address: row.address,
    postalCode: row.postalCode,
    referrer: row.referrer,
    status: row.status,
    invitationSentAt: row.invitationSentAt,
    passwordSetAt: row.passwordSetAt,
    revokedAt: row.revokedAt,
    createdAt: row.createdAt,
  }
}

/**
 * Map a backend audit row → AuditEvent.
 * - type: passthrough if a valid AuditType, otherwise OMIT.
 * - details: backend sends object|null — stringify when present, else OMIT
 *   (AuditEvent.details is string, not object).
 */
export function toAuditEvent(row: BackendAuditRow): AuditEvent {
  const ev: AuditEvent = {
    id: row.id,
    label: row.label,
    date: row.date,
    actor: row.actor,
  }
  if (isAuditType(row.type)) {
    ev.type = row.type
  }
  if (row.details != null) {
    ev.details = JSON.stringify(row.details)
  }
  return ev
}

// ── Paginated response helper ──────────────────────────────────────────────

interface ListResult<T> {
  rows: T[]
  total: number
}

interface ListWithCounts<T> extends ListResult<T> {
  counts: { pending: number; approved: number; rejected: number }
}

// ── Query-string builder ─────────────────────────────────────────────────
// One field list drives both the param type (via `typeof ... [number]` below)
// and the query string, so widening the list cannot leave a stale qs.set
// behind the way sortBy/sortDir once were.
export function buildListQueryString<T extends { page: number; pageSize: number }>(
  params: T,
  fields: readonly (keyof T & string)[],
): string {
  const qs = new URLSearchParams()
  qs.set('page', String(params.page))
  qs.set('limit', String(params.pageSize))
  for (const field of fields) {
    const value = params[field] as unknown
    if (value && (field !== 'status' || value !== 'all')) qs.set(field, String(value))
  }
  return qs.toString()
}

// ── User wrappers ──────────────────────────────────────────────────────────

const USER_LIST_FIELDS = ['search', 'role', 'status', 'sortBy', 'sortDir'] as const

/** List row: like toMockUser but keeps team_leader. */
function toListRow(row: BackendUserRow): UserListRow {
  const roles = row.roles.flatMap((r) => (isListRole(r.name) ? [r.name] : []))
  return { ...toMockUser(row), roles }
}

export async function realListUsers(
  params: Pick<ListParams, 'page' | 'pageSize' | typeof USER_LIST_FIELDS[number]>,
): Promise<ListResult<UserListRow>> {
  const qs = buildListQueryString(params, USER_LIST_FIELDS)
  const raw = await api.get<BackendUserRow[]>(`/api/users?${qs}`) as unknown as {
    data: BackendUserRow[]
    meta?: { total?: number }
  }
  const total = raw.meta?.total ?? 0
  return { rows: (raw.data ?? []).map(toListRow), total }
}

export async function realGetUser(id: string): Promise<MockUser | null> {
  const res = await api.get<BackendUserRow>(`/api/users/${id}`)
  return res.data ? toMockUser(res.data) : null
}

export async function realGetUserAudit(id: string): Promise<AuditEvent[]> {
  const res = await api.get<BackendAuditRow[]>(`/api/users/${id}/audit`)
  return (res.data ?? []).map(toAuditEvent)
}

export interface CreateUserInput {
  name: string
  email: string
  roles: Role[]
}

export interface CreateUserResult {
  user: { id: string; email: string; name: string }
  initialPassword: string
}

export async function realCreateUser(input: CreateUserInput): Promise<CreateUserResult> {
  const res = await api.post<CreateUserResult>('/api/users', input)
  return res.data!
}

export interface UpdateUserInput {
  name?: string
  email?: string
  isActive?: boolean
  forcePasswordChange?: boolean
}

export async function realUpdateUser(id: string, input: UpdateUserInput): Promise<void> {
  await api.put(`/api/users/${id}`, input)
}

export async function realSetRoles(id: string, roles: Role[]): Promise<void> {
  await api.put(`/api/users/${id}/roles`, { roles })
}

export async function realActivate(id: string): Promise<void> {
  await realUpdateUser(id, { isActive: true })
}

export async function realDeactivate(id: string): Promise<void> {
  await realUpdateUser(id, { isActive: false })
}

export async function realResetPassword(id: string): Promise<{ initialPassword: string }> {
  const res = await api.post<{ initialPassword: string }>(`/api/users/${id}/reset-password`)
  return res.data!
}

export async function realRevokeSessions(id: string): Promise<void> {
  await api.delete(`/api/users/${id}/sessions`)
}

export async function realChangeEmail(id: string, email: string): Promise<void> {
  await api.put(`/api/users/${id}/email`, { email })
}

// ── Advisor registration wrappers ──────────────────────────────────────────

const REGISTRATION_LIST_FIELDS = ['search', 'status', 'sortBy', 'sortDir'] as const

export async function realListRegistrations(
  params: Pick<ListParams, 'page' | 'pageSize' | typeof REGISTRATION_LIST_FIELDS[number]>,
): Promise<ListWithCounts<MockRegistration>> {
  const qs = buildListQueryString(params, REGISTRATION_LIST_FIELDS)
  const res = await api.get<BackendRegistrationRow[]>(`/api/advisor-registrations?${qs}`)
  const raw = res as unknown as {
    data: BackendRegistrationRow[]
    meta?: { total?: number }
    counts?: { pending: number; approved: number; rejected: number }
  }
  const total = raw.meta?.total ?? 0
  const counts = raw.counts ?? { pending: 0, approved: 0, rejected: 0 }
  return { rows: (raw.data ?? []).map(toMockRegistration), total, counts }
}

export async function realGetRegistration(id: string): Promise<MockRegistration | null> {
  const res = await api.get<BackendRegistrationRow>(`/api/advisor-registrations/${id}`)
  return res.data ? toMockRegistration(res.data) : null
}

export async function realCreateRegistration(
  input: RegistrationInput,
): Promise<MockRegistration> {
  const res = await api.post<BackendRegistrationRow>('/api/advisor-registrations', input)
  return toMockRegistration(res.data!)
}

export async function realUpdateRegistration(
  id: string,
  input: RegistrationInput,
): Promise<MockRegistration> {
  const res = await api.put<BackendRegistrationRow>(`/api/advisor-registrations/${id}`, input)
  return toMockRegistration(res.data!)
}

export async function realApprove(id: string): Promise<{ warning?: 'EMAIL_FAILED' }> {
  const res = await api.post<{
    registration?: BackendRegistrationRow
    warning?: string
    emailSent?: boolean
  }>(`/api/advisor-registrations/${id}/approve`)
  const raw = res.data
  const warning =
    raw?.warning === 'EMAIL_FAILED' || raw?.emailSent === false
      ? ('EMAIL_FAILED' as const)
      : undefined
  return warning ? { warning } : {}
}

export async function realReject(id: string): Promise<void> {
  await api.post(`/api/advisor-registrations/${id}/reject`)
}

export async function realResend(profileId: string): Promise<void> {
  await api.post(`/api/advisor-registrations/profiles/${profileId}/resend-invitation`)
}

export async function realRevokeAdvisor(id: string): Promise<void> {
  await api.post(`/api/advisor-registrations/${id}/revoke`)
}

export async function realReopen(id: string): Promise<void> {
  await api.post(`/api/advisor-registrations/${id}/reopen`)
}
